import apiClient from './AuthService';

export interface HandDoesntLieChallenge {
  hand_doesnt_lie_challenge_id?: string;
  project_id: string;
  user_id?: string;
  member_id: string;
  member_name?: string;
  image_url_1?: string | null;
  image_url_2?: string | null;
  image_url_3?: string | null;
  image_url_4?: string | null;
  image_url_5?: string | null;
  image_url_6?: string | null;
  image_url_7?: string | null;
  image_url_8?: string | null;
  image_url_9?: string | null;
  image_url_10?: string | null;
  comment?: string;
  created_at?: string;
  updated_at?: string;
}

export interface HandDoesntLieChallengeResponse {
  hand_doesnt_lie_challenge_id: string;
  message: string;
}

export interface HandDoesntLieChallengeData {
  data: HandDoesntLieChallenge | HandDoesntLieChallenge[];
}

export interface ImageUploadResponse {
  success: boolean;
  url: string;
  filename: string;
}

// Upload image to /images endpoint
export const uploadImage = async (file: File): Promise<ImageUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('image', file);

    const response = await apiClient.post<ImageUploadResponse>('/images', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return response.data;
  } catch (error: any) {
    console.error('❌ Image Upload Error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Error al subir la imagen');
  }
};

// Save HandDoesntLie challenge with up to 10 images
export const saveHandDoesntLieChallenge = async (
  projectId: string,
  memberId: string,
  imageUrls: (string | null)[],
  comment?: string
): Promise<HandDoesntLieChallengeResponse> => {
  try {
    const requestData: Record<string, string | null> = {
      project_id: projectId,
      member_id: memberId,
      comment: comment || ''
    };

    // Add image URLs (up to 10)
    for (let i = 0; i < 10; i++) {
      requestData[`image_url_${i + 1}`] = imageUrls[i] || null;
    }

    console.log('📤 Sending HandDoesntLie data:', requestData);

    const response = await apiClient.post<HandDoesntLieChallengeResponse>(
      '/quiz/hand-doesnt-lie-challenge',
      requestData
    );
    return response.data;
  } catch (error: any) {
    console.error('❌ HandDoesntLie API Error:', {
      status: error.response?.status,
      data: error.response?.data,
      fullError: error
    });

    if (error.response?.data?.error && typeof error.response.data.error === 'string') {
      throw new Error(error.response.data.error);
    }
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw error;
  }
};

// Get challenges by member
export const getMemberHandDoesntLieChallenges = async (
  memberId: string
): Promise<HandDoesntLieChallenge[]> => {
  try {
    const response = await apiClient.get<HandDoesntLieChallengeData>(
      `/quiz/member/${memberId}/hand-doesnt-lie-challenges`
    );

    if (Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return response.data.data ? [response.data.data] : [];
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    console.error('❌ Error fetching member challenges:', error);
    throw error;
  }
};

// Get challenges by project
export const getProjectHandDoesntLieChallenges = async (
  projectId: string
): Promise<HandDoesntLieChallenge[]> => {
  try {
    const response = await apiClient.get<HandDoesntLieChallengeData>(
      `/quiz/project/${projectId}/hand-doesnt-lie-challenges`
    );

    if (Array.isArray(response.data.data)) {
      return response.data.data;
    }
    return response.data.data ? [response.data.data] : [];
  } catch (error: any) {
    if (error.response?.status === 404) {
      return [];
    }
    console.error('❌ Error fetching project challenges:', error);
    throw error;
  }
};

// Helper function to extract image URLs from a challenge to an array
export const getImageUrlsFromChallenge = (challenge: HandDoesntLieChallenge): (string | null)[] => {
  return [
    challenge.image_url_1 || null,
    challenge.image_url_2 || null,
    challenge.image_url_3 || null,
    challenge.image_url_4 || null,
    challenge.image_url_5 || null,
    challenge.image_url_6 || null,
    challenge.image_url_7 || null,
    challenge.image_url_8 || null,
    challenge.image_url_9 || null,
    challenge.image_url_10 || null
  ];
};
